import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { RegulaminEditFormModel } from './form-group/rule-edit-form.model';
import {
  Regulamin,
  RegulaminEditRequestBody,
} from '../../../../../shared/models/regulamin';
import { RuleEditFormBuilder } from './form-group/rule-edit-form.builder';
import { ToastSeverity } from '../../../../../shared/enums/toast-severity.enum';
import { FormsUtils } from '../../../../../shared/utils/forms.utils';
import { APP_PATHS } from '../../../../../core/config/app-paths';
import { Card } from 'primeng/card';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { EditorModule } from 'primeng/editor';
import { RulesApi } from '../../../../../shared/services/api/rules.api';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import {
  HttpResponseErrorSource,
  HttpResponseService,
} from '../../../../../core/services/http-error-response.service';
import { RequiredLabelDirective } from '../../../../../shared/directive/required-label.directive';
import {RemoveQuillTooltipDirective} from '../../../../../shared/directive/remove-quill-tooltip.directive';
import {ControlErrorDirective} from '../../../../../shared/directive/control-error.directive';

@Component({
  selector: 'app-admin-panel-rule-edit',
  imports: [
    ReactiveFormsModule,
    Card,
    Button,
    DatePicker,
    InputTextModule,
    DatePickerModule,
    EditorModule,
    RequiredLabelDirective,
    RemoveQuillTooltipDirective,
    ControlErrorDirective,
  ],
  templateUrl: './admin-panel-rule-edit.html',
  styleUrl: './admin-panel-rule-edit.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelRuleEdit implements OnInit {
  protected readonly isSubmitting = signal(false);
  readonly rule = input<Regulamin>();

  readonly #router = inject(Router);
  readonly #messageService = inject(MessageService);
  readonly #rulesApi = inject(RulesApi);
  #httpResponseService = inject(HttpResponseService);

  protected formGroup = signal<FormGroup<RegulaminEditFormModel> | null>(null);

  protected minDate = computed(() => {
    const dataOd = this.rule()?.dataOd;
    return dataOd ? new Date(dataOd) : null;
  });

  ngOnInit(): void {
    if (this.rule()) {
      this.formGroup.set(RuleEditFormBuilder.createFormGroup(this.rule()!));
    }
  }

  #handleSuccess(): void {
    this.#httpResponseService.showSuccessMessage(
      'Zaktualizowano wersję regulaminu',
    );

    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.rules}`,
    ]);
  }

  protected onSave(): void {
    this.formGroup()!.markAllAsTouched();
    this.formGroup()!.updateValueAndValidity();

    if (!this.formGroup() || !FormsUtils.isReadyForSave(this.formGroup()!)) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Wystąpił błąd podczas walidacji formularza',
      });
      return;
    }

    const value = this.formGroup()!.getRawValue();
    const payload: RegulaminEditRequestBody = {
      dataOd: value.dataOd,
      dataDo: value.dataDo,
      tresc: value.tresc!,
    };

    this.isSubmitting.set(true);
    this.#rulesApi
      .updateRule$(value.id!, payload)
      .pipe(
        tap((response) => {
          this.#handleSuccess();
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas aktualizacji danych',
            HttpResponseErrorSource.MESSAGE_FROM_RESPONSE,
          );
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  protected onCancel(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.rules}`,
    ]);
  }
}
